import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { SymbolService } from '@easy-lib/modules/shared/services/symbol.service';
import { LsService } from '@easy-lib/modules/shared/services/Ls.service';
import { MarketDataService } from '@easy-lib/modules/shared/services/market-data.service';
import { activeStockLsData } from '@easy-lib/tools/ls-tool';
import { getColorOfSymbolState } from '@easy-lib/tools/symbol-state.tool';
import {
	defaultMarketData,
	ISymbol,
	ISymbolData,
	ISymbolMarketData
} from '@easy-lib/modules/shared/models/symbol.model';
import { LsSchemaType } from '@easy-lib/modules/shared/models/ls.model';
import { MessageFacade } from '@easy-lib/state/messages';
import { Router } from '@angular/router';

@Injectable({
	providedIn: 'root'
})
export class MobileActiveSymbolService implements OnDestroy {
	private activeSymbolSub = new BehaviorSubject<ISymbolData>(null);
	public activeSymbolObs$ = this.activeSymbolSub.asObservable();

	private activeSymbolIsin: string;
	private activeSymbolLsSubId: string;
	private subs: Subscription[] = [];

	constructor(
		private readonly lsService: LsService,
		private readonly symbolService: SymbolService,
		private readonly marketDataService: MarketDataService,
		private readonly messageFacade: MessageFacade,
		private router: Router
	) {}

	public get activeStockIsin(): string {
		return this.activeSymbolIsin;
	}

	public set activeStockIsin(value: string) {
		if (this.activeSymbolLsSubId)
			this.unsubscribeBySymbolIsinChange().then(() => {
				this.getMarketData(value);
			});
		else this.getMarketData(value);
	}

	getMarketData(isin: string) {
		this.activeSymbolIsin = isin;
		this.setSymbolData();
		this.marketDataService.callMarketDataIfLsNotConnected(() => this.getStockDetailsMarketData());
		this.subscribeToActiveSymbolLS();
	}

	ngOnDestroy(): void {
		this.subs.forEach(s => s.unsubscribe());
	}

	private async setSymbolData() {
		const symbol = await this.symbolService.getSymbol(this.activeSymbolIsin.toUpperCase());
		this.activeSymbolSub.next({ symbol, marketData: defaultMarketData } as ISymbolData);
	}

	private subscribeToActiveSymbolLS(): void {
		this.lsService.subscribeToLs(LsSchemaType.ActiveStock, this.activeSymbolIsin).subscribe(data => {
			if (data.inst === this.activeSymbolIsin) {
				this.marketDataService.lsDataIsReceived(this.getStockDetailsMarketData.name);
				this.activeSymbolLsSubId = data.subId;
				const marketData: ISymbolMarketData = activeStockLsData(data.val);
				marketData.closingPriceVarPercent =
					this.symbolService.calculateClosingPriceVarPercent(
						marketData.closingPrice,
						marketData.feeOfPreviousDaysClosingPrice,
						marketData.totalNumberOfSharesTraded
					) || 0;
				const symbol: ISymbol = this.activeSymbolSub.getValue()?.symbol;
				this.activeSymbolSub.next({ symbol, marketData } as ISymbolData);
			}
		});
	}

	async unsubscribeBySymbolIsinChange() {
		if (this.activeSymbolLsSubId) this.lsService.unsubscribeSchemaById(this.activeSymbolLsSubId);
		this.subs.forEach(s => s.unsubscribe());
	}

	getStockDetailsMarketData(): void {
		const sub = this.marketDataService
			.GetSymbolDetailsHeader(this.activeStockIsin)
			.pipe(
				tap(data => {
					const subData = this.activeSymbolSub.getValue();
					const subSymbol: ISymbol = this.activeSymbolSub.getValue()?.symbol;
					const symbol = subData.symbol || subSymbol;
					const marketData = subData.marketData ?? defaultMarketData;
					marketData.lastTradedPrice = data.lastTradedPrice;
					marketData.feeOfPreviousDaysClosingPrice = data.feeOfPreviousDaysClosingPrice;
					marketData.lastTradedPriceVar = this.symbolService.calculateLastTradedPriceVar(
						data.lastTradedPrice,
						data.feeOfPreviousDaysClosingPrice
					);
					marketData.lastTradedPriceVarPercent = data.priceVar;
					marketData.stateColor = getColorOfSymbolState(data.stateCode);
					marketData.closingPrice = data.closingPrice;
					marketData.closingPriceVarPercent = this.symbolService.calculateClosingPriceVarPercent(
						data.closingPrice,
						data.feeOfPreviousDaysClosingPrice,
						data.totalNumberOfSharesTraded
					);
					marketData.totalNumberOfSharesTraded = data.totalNumberOfSharesTraded;
					marketData.stateCode = data.stateCode;

					this.activeSymbolSub.next({ symbol, marketData } as ISymbolData);
				}),
				catchError((error, caught) => caught),
				finalize(() => sub.unsubscribe())
			)
			.subscribe();
	}
	subscribeForActiveSymbol() {
		this.messageFacade.getStockChange$().subscribe(isin => {
			if (isin?.length > 0) {
				this.router.navigate(['/stock-details', isin]);
			}
		});
	}
}
